// F1 Standings Exploration Page
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useF1Championship } from '../../hooks/useF1Championship';
import F1PageTemplate from '../../components/f1/F1PageTemplate';
import { TableChart } from '../../components/charts';
import LoadingSpinner from '../../components/LoadingSpinner';

const F1StandingsExplorationPage = () => {
  const { year } = useParams();
  const { data, loading, error } = useF1Championship();

  const [currentRound, setCurrentRound] = useState(1);
  const [showConstructors, setShowConstructors] = useState(false);

  // Get races for this season
  const seasonRaces = data?.seasonRaces?.filter(race => race.year == year) || [];
  
  // Sort races by round
  const sortedRaces = [...seasonRaces].sort((a, b) => a.round - b.round);
  const totalRounds = sortedRaces.length;

  // Ensure currentRound is valid
  const validRound = Math.max(1, Math.min(currentRound, totalRounds || 1));
  
  // Calculate standings up to current round
  const standingsUpToRound = useMemo(() => {
    const racesUpToRound = sortedRaces.slice(0, validRound);
    const raceIdsUpToRound = new Set(racesUpToRound.map(r => r.raceId));
    
    // Get results for races up to current round
    const resultsUpToRound = data?.results?.filter(result => raceIdsUpToRound.has(result.raceId)) || [];
    
    // Create name maps
    const driverNameMap = {};
    const constructorNameMap = {};
    
    if (data?.drivers) {
      data.drivers.forEach(driver => {
        driverNameMap[driver.driverId] = `${driver.forename} ${driver.surname}`;
      });
    }
    
    if (data?.constructors) {
      data.constructors.forEach(constructor => {
        constructorNameMap[constructor.constructorId] = constructor.name;
      });
    }
    
    if (showConstructors) {
      // Calculate constructor standings
      const constructorPoints = {};
      
      resultsUpToRound.forEach(result => {
        const constructorId = result.constructorId;
        if (!constructorPoints[constructorId]) {
          constructorPoints[constructorId] = {
            constructorId,
            constructorName: constructorNameMap[constructorId] || 'Unknown',
            points: 0,
            wins: 0
          };
        }
        constructorPoints[constructorId].points += result.points || 0;
        if (result.position === 1) {
          constructorPoints[constructorId].wins += 1;
        }
      });

      return Object.values(constructorPoints).sort((a, b) => b.points - a.points);
    } else {
      // Calculate driver standings
      const driverPoints = {};
      
      resultsUpToRound.forEach(result => {
        const driverId = result.driverId;
        if (!driverPoints[driverId]) {
          driverPoints[driverId] = {
            driverId,
            driverName: driverNameMap[driverId] || 'Unknown',
            points: 0,
            wins: 0,
            constructorName: constructorNameMap[result.constructorId] || 'Unknown'
          };
        }
        driverPoints[driverId].points += result.points || 0;
        if (result.position === 1) {
          driverPoints[driverId].wins += 1;
        }
      });

      return Object.values(driverPoints).sort((a, b) => b.points - a.points);
    }
  }, [sortedRaces, validRound, showConstructors, data?.results, data?.drivers, data?.constructors]);

  if (loading) {
    return (
      <div className="min-h-screen theme-bg-secondary flex items-center justify-center">
        <LoadingSpinner message="Loading Standings Data..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen theme-bg-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-semibold theme-accent mb-4">Error Loading Standings</div>
          <div className="theme-secondary">{error}</div>
          <Link to={`/f1/seasons/${year}`} className="mt-4 inline-block px-4 py-2 theme-accent text-white rounded-md hover:opacity-80">
            Back to Season
          </Link>
        </div>
      </div>
    );
  }

  // Find specific season
  const season = data?.seasonSummaries?.find(s => s.year == year);
  
  if (!season) {
    return (
      <div className="min-h-screen theme-bg-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-semibold theme-primary mb-4">Season Not Found</div>
          <div className="theme-secondary mb-4">Season {year} could not be found.</div>
          <Link to="/f1/seasons" className="inline-block px-4 py-2 theme-accent text-white rounded-md hover:opacity-80">
            Back to Seasons
          </Link>
        </div>
      </div>
    );
  }

  if (seasonRaces.length === 0) {
    return (
      <div className="min-h-screen theme-bg-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-semibold theme-primary mb-4">No Races Found</div>
          <div className="theme-secondary mb-4">No race data available for season {year}.</div>
          <Link to={`/f1/seasons/${year}`} className="inline-block px-4 py-2 theme-accent text-white rounded-md hover:opacity-80">
            Back to Season
          </Link>
        </div>
      </div>
    );
  }

  // Sync currentRound if needed
  if (validRound !== currentRound) {
    setCurrentRound(validRound);
  }

  const currentRace = sortedRaces[validRound - 1];

  const handlePrevious = () => {
    setCurrentRound(prev => Math.max(1, prev - 1));
  };

  const handleNext = () => {
    setCurrentRound(prev => Math.min(totalRounds, prev + 1));
  };

  const handleRoundSelect = (round) => {
    setCurrentRound(round);
  };

  return (
    <F1PageTemplate
      title={`${year} Standings Exploration`}
      subtitle={`Step through each race to see how the ${showConstructors ? 'constructor' : 'driver'} championship evolved`}
      summary={['Race-by-race progression', 'Standings evolution', 'Championship battle']}
    >
      <div className="space-y-6">
        {/* Controls */}
        <div className="card">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Toggle between drivers/constructors */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowConstructors(false)}
                className={`font-medium transition-colors ${
                  !showConstructors
                    ? 'btn-primary'
                    : 'btn-secondary'
                }`}
              >
                Drivers
              </button>
              <button
                onClick={() => setShowConstructors(true)}
                className={`font-medium transition-colors ${
                  showConstructors
                    ? 'btn-primary'
                    : 'btn-secondary'
                }`}
              >
                Constructors
              </button>
            </div>
            <div>
              <Link 
                  to={`/f1/races/${currentRace.raceId}`}
                  className="theme-primary hover:theme-accent hover:underline card-title"
                >
                  {currentRace.name}
              </Link>
            </div>

            {/* Navigation */}
            <div className="flex items-center space-x-4">
              <button
                onClick={handlePrevious}
                disabled={validRound === 1}
                className="btn-navigation"
              >
                Previous
              </button>
              <div className="text-center">
                <div className="text-sm theme-secondary">Round</div>
                <div className="text-xl font-bold theme-primary">
                  {validRound} / {totalRounds}
                </div>
              </div>
              <button
                onClick={handleNext}
                disabled={validRound === totalRounds}
                className="btn-navigation"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* Round Selector */}
        <div className="card">
          <h3 className="card-title mb-4">Select Round</h3>
          <div className="flex flex-wrap justify-between">
            {sortedRaces.map((race, index) => (
              <button
                key={race.raceId}
                onClick={() => handleRoundSelect(index + 1)}
                className={`font-medium transition-colors ${
                  index + 1 === validRound
                    ? 'btn-primary'
                    : 'btn-secondary'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>

        {/* Current Race Info */}
        {currentRace && (
          <div className="card">
            <h3 className="card-title mb-4">Current Race: Round {validRound}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="text-sm theme-secondary">Race Name</div>
                <div className="text-lg font-semibold theme-primary">
                  <Link 
                    to={`/f1/races/${currentRace.raceId}`}
                    className="theme-primary hover:theme-accent hover:underline"
                  >
                    {currentRace.name}
                  </Link>
                </div>
              </div>
              <div>
                <div className="text-sm theme-secondary">Circuit</div>
                <div className="text-lg font-semibold theme-primary">
                  <Link 
                    to={`/f1/circuits/${currentRace.circuitId}`}
                    className="theme-primary hover:theme-accent hover:underline"
                  >
                    {currentRace.circuit}
                  </Link>
                </div>
              </div>
              <div>
                <div className="text-sm theme-secondary">Date</div>
                <div className="text-lg font-semibold theme-primary">{currentRace.date}</div>
              </div>
            </div>
          </div>
        )}

        
        {/* Standings Table */}
        <div className="card">
          <h3 className="card-title mb-4">
            {showConstructors ? 'Constructor' : 'Driver'} Standings After Round {validRound}
          </h3>
          <TableChart
            data={standingsUpToRound.map((item, index) => ({
              position: index + 1,
              name: showConstructors ? item.constructorName : item.driverName,
              points: item.points,
              wins: item.wins,
              ...(showConstructors ? {} : { constructor: item.constructorName })
            }))}
            config={{
              columns: showConstructors ? [
                { key: 'position', label: 'Pos', width: 60 },
                { key: 'name', label: 'Constructor', width: 200 },
                { key: 'points', label: 'Points', width: 100 },
                { key: 'wins', label: 'Wins', width: 80 }
              ] : [
                { key: 'position', label: 'Pos', width: 60 },
                { key: 'name', label: 'Driver', width: 200 },
                { key: 'constructor', label: 'Team', width: 180 },
                { key: 'points', label: 'Points', width: 100 },
                { key: 'wins', label: 'Wins', width: 80 }
              ],
              sortable: false,
              pagination: false,
              showHeader: true
            }}
          />
        </div>

        {/* Back to Season */}
        <div className="text-center">
          <Link
            to={`/f1/seasons/${year}`}
            className="inline-block px-6 py-3 theme-accent text-white rounded-md hover:opacity-80"
          >
            Back to Season Overview
          </Link>
        </div>
      </div>
    </F1PageTemplate>
  );
};

export default F1StandingsExplorationPage;
